import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { callLambda, getActionFromLambdaResults } from '../../../../helpers/lambda.js';

import NSJobComponent from '../../job.js';

import JobSchema from '../../../../models/job-schema.js';
import CommercialRelationship from '../../../../models/commercial-relationship.js';
import '../../../../ns-elements/ns-depth-input.js';
import { renderFormField } from '../../../../helpers/form-field.js';
import '../../../../ns-elements/ns-job-layout.js';
import { nsFormFieldStyles } from '../../../../ns-styles/index.js';
import { toDateInputValue } from '../../../../helpers/format.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/company/relationship-edit.json';
import { jobSchema } from '../../job-schema.js';

const DESCRIPTION_MAX_LENGTH = 150;

@customElement(NSCompanyRelationshipEdit.is)
export default class NSCompanyRelationshipEdit extends NSJobComponent {
  static get is() {
    return 'ns-company-relationship-edit';
  }

  @state()
  private __relationship?: CommercialRelationship;

  @state()
  private __description = '';

  @state()
  private __contractValue?: number;

  @state()
  private __reviewDate = '';
  private __loadedReviewDate = '';

  @state()
  private __requestedDepth = 0;
  private __loadedRequestedDepth = 0;

  @state()
  private __saving = false;

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { save: { type: 'hidden' }, cancel: { type: 'hidden' } } });
  }

  static styles = [...super.styles, nsFormFieldStyles];

  async connectedCallback() {
    try {
      await super.connectedCallback();

      if (!this._db) throw new Error('Unable to find db service');

      if (!this._auth) throw new Error('Unable to find auth service');

      this.__relationship = await this.loadInput('comRelationship', 'relationshipId');

      this.__description = this.__relationship.description ?? '';
      this.__contractValue = this.__relationship.contractValue;
      this.__reviewDate = toDateInputValue(this.__relationship.reviewDate);
      this.__loadedReviewDate = this.__reviewDate;
      this.__requestedDepth = this.__relationship.requestedDepth ?? 0;
      this.__loadedRequestedDepth = this.__requestedDepth;
    } catch (err: unknown) {
      this.failJob(err, 'Unexpected error occurred while loading the relationship');
    }
  }

  private __onDescriptionInput(evt: InputEvent): void {
    this.__description = (evt.target as HTMLInputElement).value;
  }

  private __onContractValueInput(evt: InputEvent): void {
    const value = (evt.target as HTMLInputElement).value;
    this.__contractValue = value ? Number(value) : undefined;
  }

  private __onReviewDateInput(evt: InputEvent): void {
    this.__reviewDate = (evt.target as HTMLInputElement).value;
  }

  private get __descriptionError(): string | undefined {
    return this.__description.trim() ? undefined : 'Enter a description';
  }

  private get __reviewDateChanged(): boolean {
    return this.__reviewDate !== this.__loadedReviewDate;
  }

  private get __reviewDateError(): string | undefined {
    const today = toDateInputValue(new Date());
    return this.__reviewDateChanged && this.__reviewDate && this.__reviewDate < today
      ? 'Pick today or a later date'
      : undefined;
  }

  private async __save() {
    this.__saving = true;

    try {
      if (!this._auth) throw new Error('Missing auth service');
      if (!this.__relationship?.id) throw new Error('Missing required relationship');
      this.loading('Saving Relationship');
      const lambdaResult = await callLambda(
        'company/relationship-update',
        'POST',
        this._auth.token,
        JSON.stringify({
          relationshipId: this.__relationship.id,
          description: this.__description.trim(),
          contractValue: this.__contractValue ?? null,
          contractCurrency: this.__contractValue === undefined ? undefined : 'GBP',
          // Sent only when changed: a new review date restarts the relationship's review cycle, and none stops it
          reviewDate: this.__reviewDateChanged ? this.__reviewDate || null : undefined,
          // Sent only when changed, so an "every tier" ask the input can't show survives other edits
          requestedDepth: this.__requestedDepth !== this.__loadedRequestedDepth ? this.__requestedDepth : undefined,
        }),
      );

      await getActionFromLambdaResults(lambdaResult, 'commercial_relationship_updated');
      this.loaded();
      await this.outcome('save');
    } catch (err) {
      this.loaded();
      this.failJob(err, 'Unexpected error occurred while saving the relationship');
    } finally {
      this.__saving = false;
    }
  }

  private async __cancel() {
    this.outcome('cancel');
  }

  render() {
    return html`
      <ns-job-layout>
        <p slot="intro">Edit relationship: ${this.__relationship?.description ?? ''}</p>
        <div class="job-panel">
          ${renderFormField({
            id: 'description',
            label: 'Description',
            error: this.__descriptionError,
            control: html`
              <input
                id="description"
                type="text"
                required
                maxlength=${DESCRIPTION_MAX_LENGTH}
                aria-invalid=${this.__descriptionError ? 'true' : 'false'}
                aria-describedby=${this.__descriptionError ? 'description-error' : ''}
                .value=${this.__description}
                @input=${this.__onDescriptionInput}
              />
            `,
          })}
          ${renderFormField({
            id: 'contractValue',
            label: 'Contract value (£)',
            control: html`
              <input
                id="contractValue"
                type="number"
                .value=${this.__contractValue ?? ''}
                @input=${this.__onContractValueInput}
              />
            `,
          })}
          ${renderFormField({
            id: 'reviewDate',
            label: 'Review date',
            hint: 'Leave blank to stop scheduled reviews.',
            error: this.__reviewDateError,
            control: html`
              <input
                id="reviewDate"
                type="date"
                min=${toDateInputValue(new Date())}
                aria-invalid=${this.__reviewDateError ? 'true' : 'false'}
                aria-describedby=${this.__reviewDateError ? 'reviewDate-error' : ''}
                .value=${this.__reviewDate}
                @input=${this.__onReviewDateInput}
              />
            `,
          })}
          <ns-depth-input
            .value=${this.__requestedDepth}
            @depth-change=${(e: CustomEvent<{ value: number }>) => (this.__requestedDepth = e.detail.value)}
          >
            Raising this may prompt suppliers who've already shared their chain to share further down to satisfy the new
            depth.
          </ns-depth-input>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    return html`
      <wa-button
        variant="brand"
        appearance="outlined"
        type="button"
        @click=${() => this.__cancel()}
        ?disabled=${this.__saving}
      >
        Cancel
      </wa-button>
      <wa-button
        variant="brand"
        type="button"
        @click=${() => this.__save()}
        ?disabled=${this.__saving || !!this.__descriptionError || !!this.__reviewDateError}
      >
        Save
      </wa-button>
    `;
  }
}
